import { useCallback, useEffect, useMemo, useState } from "react"
import {
  createStudentRevisionGoal,
  DASHBOARD_ACTIVITY_MAX_LIMIT,
  emptyDashboardStats,
  getStudentDashboardStats,
  listStudentDashboardActivity,
  listStudentRevisionGoals,
  type StudentDashboardStats,
  type StudentRevisionGoal,
} from "../../../../api/studentDashboard"
import { getLearnerApiErrorMessage } from "../../../../auth/learner/errors"
import type {
  DashboardActivity,
  DashboardGoal,
  DashboardStatsSummary,
  NewDashboardGoalInput,
} from "../types"
import { buildDashboardStatCards } from "../utils/buildDashboardStats"
import { mapDashboardActivity } from "../utils/mapDashboardActivity"
import { DASHBOARD_QUICK_LINKS } from "../utils/dashboardStaticContent"

const toSummary = (data: StudentDashboardStats): DashboardStatsSummary => ({
  totalAttempts: data.totalAttempts || 0,
  papersInProgress: data.papersInProgress || 0,
  papersCompleted: data.papersCompleted || 0,
  aiAssessmentsCount: data.aiAssessmentsCount || 0,
  avgAiAssessmentScore:
    typeof data.avgAiAssessmentScore === "number"
      ? data.avgAiAssessmentScore
      : null,
  savedItemsCount: data.savedItemsCount || 0,
  bookmarkedCount: data.bookmarkedCount || 0,
  notesCount: data.notesCount || 0,
})

const toGoal = (goal: StudentRevisionGoal): DashboardGoal => ({
  id: goal.id,
  subjectId: goal.subjectId,
  subject: goal.subject,
  title: goal.title || goal.task || "",
  task: goal.title || goal.task || "",
  target: goal.target || "",
  currentProgress: goal.currentProgress || 0,
  targetCount: goal.targetCount || 0,
  progressPercent: goal.progressPercent ?? goal.progress ?? 0,
  dueDate: goal.dueDate,
  dueAt: goal.dueAt,
  status: goal.status || "active",
  papers: (goal.papers || []).map((paper) => ({
    id: paper.id,
    title: paper.title,
    status: paper.status,
    progressPercent: paper.progressPercent ?? 0,
  })),
})

const useDashboardData = () => {
  const [goals, setGoals] = useState<DashboardGoal[]>([])
  const [goalsLoading, setGoalsLoading] = useState(true)
  const [goalsError, setGoalsError] = useState<string | null>(null)
  const [isSavingGoal, setIsSavingGoal] = useState(false)
  const [activities, setActivities] = useState<DashboardActivity[]>([])
  const [activitiesLoading, setActivitiesLoading] = useState(true)
  const [activitiesError, setActivitiesError] = useState<string | null>(null)
  const [summary, setSummary] = useState<DashboardStatsSummary>(
    emptyDashboardStats()
  )
  const [statsLoading, setStatsLoading] = useState(true)
  const [statsError, setStatsError] = useState<string | null>(null)

  const refreshStats = useCallback(async () => {
    setStatsLoading(true)
    setStatsError(null)
    try {
      const res = await getStudentDashboardStats()
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || "Could not load dashboard stats")
      }
      setSummary(toSummary(res.data.data))
    } catch (err) {
      setSummary(emptyDashboardStats())
      setStatsError(
        getLearnerApiErrorMessage(err, "Could not load dashboard stats")
      )
    } finally {
      setStatsLoading(false)
    }
  }, [])

  const refreshGoals = useCallback(async () => {
    setGoalsLoading(true)
    setGoalsError(null)
    try {
      const res = await listStudentRevisionGoals()
      if (!res.data?.success || !Array.isArray(res.data.data)) {
        throw new Error(res.data?.message || "Could not load revision goals")
      }
      setGoals(res.data.data.map(toGoal))
    } catch (err) {
      setGoals([])
      setGoalsError(
        getLearnerApiErrorMessage(err, "Could not load revision goals")
      )
    } finally {
      setGoalsLoading(false)
    }
  }, [])

  const refreshActivities = useCallback(async () => {
    setActivitiesLoading(true)
    setActivitiesError(null)
    try {
      const res = await listStudentDashboardActivity(
        DASHBOARD_ACTIVITY_MAX_LIMIT
      )
      if (!res.data?.success || !Array.isArray(res.data.data)) {
        throw new Error(res.data?.message || "Could not load recent activity")
      }
      setActivities(res.data.data.map(mapDashboardActivity))
    } catch (err) {
      setActivities([])
      setActivitiesError(
        getLearnerApiErrorMessage(err, "Could not load recent activity")
      )
    } finally {
      setActivitiesLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshStats()
    void refreshGoals()
    void refreshActivities()
  }, [refreshStats, refreshGoals, refreshActivities])

  const stats = useMemo(() => buildDashboardStatCards(summary), [summary])

  const addGoal = useCallback(async (input: NewDashboardGoalInput) => {
    setIsSavingGoal(true)
    try {
      const res = await createStudentRevisionGoal({
        subjectId: input.subjectId,
        title: input.title,
        paperIds: input.paperIds,
        dueDate: input.dueDate,
        restartPapers: !!input.restartPapers,
      })
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || "Could not save goal")
      }
      const created = toGoal(res.data.data)
      const rank: Record<DashboardGoal["status"], number> = {
        overdue: 0,
        active: 1,
        completed: 2,
      }
      setGoals((prev) => {
        const next = [created, ...prev.filter((goal) => goal.id !== created.id)]
        next.sort((a, b) => {
          const statusDiff = (rank[a.status] ?? 9) - (rank[b.status] ?? 9)
          if (statusDiff !== 0) return statusDiff
          return (a.dueDate || "").localeCompare(b.dueDate || "")
        })
        return next
      })
    } finally {
      setIsSavingGoal(false)
    }
  }, [])

  return {
    loading: statsLoading || goalsLoading || activitiesLoading,
    statsLoading,
    statsError,
    goalsLoading,
    goalsError,
    activitiesLoading,
    activitiesError,
    isSavingGoal,
    stats,
    activities,
    goals,
    quickLinks: DASHBOARD_QUICK_LINKS,
    addGoal,
    refresh: () => {
      void refreshStats()
      void refreshGoals()
      void refreshActivities()
    },
  }
}

export default useDashboardData
